import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import {
  brandThemeCssProperties,
  contrastingInk,
  type PrequalBranding
} from '@prescreen/lib/prequal-branding'
import { cn } from '@prescreen/lib/utils'

function BrandLogo(props: { branding: PrequalBranding }): ReactNode {
  const [failed, setFailed] = useState(false)
  const showLogo = props.branding.logoUrl !== undefined && !failed

  if (!showLogo) {
    return (
      <p
        className="text-xl font-semibold"
        style={{ color: contrastingInk(props.branding.background) }}
      >
        {props.branding.displayName}
      </p>
    )
  }

  return (
    <img
      src={props.branding.logoUrl}
      alt={props.branding.displayName}
      className="mx-auto h-12 w-auto max-w-[200px] object-contain"
      onError={() => setFailed(true)}
    />
  )
}

export function useBorrowerDocumentTitle(displayName: string | undefined): void {
  useEffect(() => {
    if (!displayName) {
      return
    }
    const previous = document.title
    document.title = displayName
    return () => {
      document.title = previous
    }
  }, [displayName])
}

export function BorrowerBrandingFrame(props: {
  branding?: PrequalBranding
  children: ReactNode
  className?: string
}): ReactNode {
  useBorrowerDocumentTitle(props.branding?.displayName)

  if (!props.branding) {
    return <div className={props.className}>{props.children}</div>
  }

  const style = brandThemeCssProperties(props.branding) as CSSProperties

  return (
    <div className={cn('min-h-screen', props.className)} style={style}>
      <header className="border-b border-border bg-background px-4 py-6">
        <div className="mx-auto max-w-4xl text-center">
          <BrandLogo branding={props.branding} />
        </div>
      </header>
      {props.children}
    </div>
  )
}

export function borrowerPageShellClass(branded: boolean): string {
  return branded ? 'bg-background py-8 text-foreground' : 'min-h-screen bg-gray-50 py-8'
}

export function borrowerSpinnerClass(branded: boolean): string {
  return branded ? 'border-primary' : 'border-blue-600'
}

export const borrowerCompleteChrome = {
  loanAmountCard: (branded: boolean) =>
    branded
      ? 'bg-accent/15 border-accent'
      : 'bg-blue-50 border-blue-200',
  loanAmountTitle: (branded: boolean) =>
    branded ? 'font-semibold text-gray-900' : 'font-semibold text-blue-800',
  loanAmountValue: (branded: boolean) =>
    branded ? 'text-2xl font-bold text-gray-900' : 'text-2xl font-bold text-blue-900',
  loanAmountHint: (branded: boolean) =>
    branded ? 'text-sm text-muted-foreground' : 'text-sm text-blue-600',
  selectedLoanRow: (branded: boolean, selected: boolean) => {
    if (!selected) {
      return 'hover:bg-muted/50 border-l-4 border-l-transparent'
    }
    return branded
      ? 'bg-primary/10 border-l-4 border-l-primary'
      : 'bg-blue-50 border-l-4 border-l-blue-500'
  },
  selectedLoanCheck: (branded: boolean) =>
    branded ? 'text-primary' : 'text-blue-600',
  continueButtonClass: (branded: boolean) =>
    branded
      ? 'text-lg py-3 px-8'
      : 'bg-green-600 hover:bg-green-700 text-white text-lg py-3 px-8',
  successIconWrap: (branded: boolean) =>
    branded ? 'bg-primary/15' : 'bg-blue-100',
  successIcon: (branded: boolean) =>
    branded ? 'text-primary' : 'text-blue-600',
  infoPanel: (branded: boolean) =>
    branded
      ? 'bg-accent/15 border-accent'
      : 'bg-blue-50 border-blue-200',
  infoTitle: (branded: boolean) =>
    branded ? 'font-semibold text-gray-900 mb-2' : 'font-semibold text-blue-800 mb-2',
  infoList: (branded: boolean) =>
    branded ? 'text-sm text-gray-900 space-y-1 text-left' : 'text-sm text-blue-700 space-y-1 text-left',
  continueOutlineButton: (branded: boolean) =>
    branded ? 'w-full text-lg py-3 px-6' : 'w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-3 px-6'
}
